import type { JsonSchema7, VerticalLayout } from '@jsonforms/core';
import { createContext, useContext } from 'react';
import type { useGetFormCustomisationByParentTypes } from 'src/hooks/queries/form-configuration/useGetFormCustomisationByParentTypes';

import {
  createFormFieldElementsStore,
  type FormFieldElementsStore,
} from '../stores/formFieldElementsStore';
import {
  createFormFieldRegistryStore,
  type FormFieldRegistryStore,
} from '../stores/formFieldRegistryStore';
import type { WithParsedConditions } from './formConfigurationContracts';

type GetFormCustomisationData = NonNullable<
  ReturnType<typeof useGetFormCustomisationByParentTypes>['data']
>;

export type TypedFormFieldConfiguration = WithParsedConditions<
  GetFormCustomisationData['form_field_configuration'][number]
>;

export type TypedFormSectionConfiguration = WithParsedConditions<
  GetFormCustomisationData['form_section_configuration'][number]
>;

export type CustomisableFormDataContextState = {
  formFieldConfigurations: TypedFormFieldConfiguration[] | null;
  formFieldOrdering: GetFormCustomisationData['form_field_ordering'] | null;
  formSections: TypedFormSectionConfiguration[] | null;
  customAttributeSchema: {
    UiSchema: VerticalLayout;
    Schema: JsonSchema7;
    Id: string;
  } | null;
  fieldRegistryStore: FormFieldRegistryStore;
  fieldElementsStore: FormFieldElementsStore;
};

// Default store handles are inert fallbacks for consumers rendered outside a
// CustomisableFormDataProvider (e.g. standalone Controlled* field components in
// isolation). The only writers (CustomisableFieldWrapper) always sit under a
// provider, so these defaults never accumulate cross-form state — each real
// form instance gets its own pair via the provider's useState initialiser.
export const CustomisableFormDataContext =
  createContext<CustomisableFormDataContextState>({
    formFieldConfigurations: null,
    formFieldOrdering: null,
    formSections: null,
    customAttributeSchema: null,
    fieldRegistryStore: createFormFieldRegistryStore(),
    fieldElementsStore: createFormFieldElementsStore(),
  });

export const useCustomisableFormDataContext = () =>
  useContext(CustomisableFormDataContext);
